import { create } from 'zustand';
import useDashboardStore from './dashboardStore';

interface HistoryState {
  past: unknown[];
  future: unknown[];
  /** 组件快照序列化（去运行态字段） */
  snapshot: () => unknown;
  push: (snap?: unknown) => void;
  undo: () => void;
  redo: () => void;
  reset: () => void;
}

const MAX_HISTORY = 50;

function serialize() {
  const s = useDashboardStore.getState();
  return {
    components: JSON.parse(JSON.stringify(s.components)),
    page: s.currentPage ? JSON.parse(JSON.stringify(s.currentPage)) : null,
  };
}

function apply(snapshot: any) {
  suspended = true;
  try {
    useDashboardStore.setState({
      components: snapshot?.components ?? [],
      currentPage: snapshot?.page ?? useDashboardStore.getState().currentPage,
      dirty: true,
    });
  } finally {
    suspended = false;
  }
}

export const useDashboardHistory = create<HistoryState>((set, get) => ({
  past: [],
  future: [],

  snapshot: () => serialize(),

  push: (snap) => {
    const target = snap ?? serialize();
    set((s) => ({
      past: [...s.past.slice(-(MAX_HISTORY - 1)), target],
      future: [],
    }));
  },

  undo: () => {
    const { past, future } = get();
    if (past.length === 0) return;
    clearBatch();
    const prev = past[past.length - 1];
    const current = serialize();
    set({ past: past.slice(0, -1), future: [current, ...future].slice(0, MAX_HISTORY) });
    apply(prev);
  },

  redo: () => {
    const { past, future } = get();
    if (future.length === 0) return;
    clearBatch();
    const [next, ...rest] = future;
    const current = serialize();
    set({ past: [...past, current].slice(-MAX_HISTORY), future: rest });
    apply(next);
  },

  reset: () => {
    clearBatch();
    set({ past: [], future: [] });
  },
}));

// ============================================
// 订阅 + 批量基线（debounce）
// - suspended：undo/redo/apply 期间不记录，避免 future 被清空（R4.1）
// - batchStart：记录「本批次变更前」的状态作为撤销基线，解决首次撤销无变化（R4）
// - autoSuspended：手势历史批次（beginGestureHistory/endGestureHistory）期间关闭自动批次，
//   整段拖拽/缩放/旋转只产生 1 个撤销步（2026-10-05，就地编辑拖拽跨批次碎片问题）
// ============================================
let timer: ReturnType<typeof setTimeout> | null = null;
let suspended = false;
let autoSuspended = false;
let batchStart: unknown = null;

function clearBatch() {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  batchStart = null;
}

/** 手势开始时调用：先冲刷未入栈的自动批次，再压入「手势前」基线并暂停自动批次 */
export function beginGestureHistory() {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (batchStart !== null) {
    const b = batchStart;
    batchStart = null;
    useDashboardHistory.getState().push(b);
  }
  autoSuspended = true;
  useDashboardHistory.getState().push();
}

/** 手势结束时调用：恢复自动批次 */
export function endGestureHistory() {
  autoSuspended = false;
}

useDashboardStore.subscribe((state, prev) => {
  if (suspended || autoSuspended) return;
  if (state.components === prev.components) return;
  if (state.saving) return;

  // 记录批次起点（第一次变化前的状态）
  if (batchStart === null) {
    batchStart = serializeFrom(prev);
  }
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    if (suspended || batchStart === null) return;
    useDashboardHistory.getState().push(batchStart);
    batchStart = null;
  }, 400);
});

function serializeFrom(state: any) {
  return {
    components: JSON.parse(JSON.stringify(state.components)),
    page: state.currentPage ? JSON.parse(JSON.stringify(state.currentPage)) : null,
  };
}
